import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { getSupabaseClient } from '../../lib/supabase';
import { createClient } from '@supabase/supabase-js';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Briefcase, 
  Wallet, 
  LogOut, 
  RefreshCw, 
  Clock, 
  Calendar, 
  MapPin, 
  Phone, 
  CheckCircle2, 
  XCircle, 
  Play, 
  AlertCircle, 
  Loader2, 
  IndianRupee, 
  QrCode, 
  Share2, 
  Copy, 
  ExternalLink, 
  X, 
  Check, 
  ChevronRight,
  TrendingDown,
  TrendingUp,
  Percent,
  Receipt,
  User,
  ShieldAlert,
  ArrowRight,
  Bell,
  Star,
  Smartphone,
  Info,
  CheckCheck
} from 'lucide-react';
import { ServiceBillingPanel } from '../../components/services/ServiceBillingPanel';
import { JobKitManager } from '../../components/services/JobKitManager';
import { ProfessionalLedgerDrawer } from '../../components/services/ProfessionalLedgerDrawer';
import { ServiceBillPrintModal } from '../../components/services/ServiceBillPrintModal';
import { useAuth } from '../../contexts/AuthContext';

export type BookingStatus = 
  | 'requested' 
  | 'assigned' 
  | 'accepted' 
  | 'rejected' 
  | 'in_progress' 
  | 'completed' 
  | 'cancelled' 
  | 'no_show';

export interface ServiceBookingItem {
  id: string;
  tenant_id: string;
  branch_id: string;
  booking_number: string;
  customer_name: string;
  customer_phone?: string | null;
  customer_address?: string | null;
  category_id?: string | null;
  catalog_item_id: string;
  professional_id?: string | null;
  booking_mode: 'scheduled' | 'on_demand';
  scheduled_at?: string | null;
  status: BookingStatus;
  quoted_price: number;
  final_price?: number | null;
  invoice_id?: string | null;
  start_otp?: string | null;
  complete_otp?: string | null;
  cancelled_reason?: string | null;
  payment_status?: string | null;
  payment_mode?: string | null;
  paid_at?: string | null;
  razorpay_payment_link_id?: string | null;
  razorpay_payment_link_short_url?: string | null;
  created_at: string;
  assigned_at?: string | null;
  accepted_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  quantity?: number | null;
  customer_notes?: string | null;
  service_catalog_items?: {
    id: string;
    service_name: string;
    base_price: number;
  } | null;
}

export interface ServiceProfessionalIdentity {
  id: string;
  service_provider_id: string;
  tenant_id: string;
  branch_id?: string | null;
  full_name: string;
  phone?: string | null;
  commission_percent: number;
  rating?: number | null;
}

export interface WalletAccount {
  id: string;
  tenant_id?: string | null;
  owner_type: string;
  owner_id: string;
  account_label?: string | null;
  current_balance: number;
  currency: string;
}

export interface LedgerTransaction {
  id: string;
  wallet_account_id: string;
  transaction_type: 'credit' | 'debit';
  amount: number;
  balance_after?: number | null;
  reference_type?: string | null;
  reference_id?: string | null;
  description?: string | null;
  created_at: string;
}

export interface NotificationItem {
  id: string;
  channel: 'in_app' | 'push';
  event_code: string;
  rendered_body: string;
  status: 'queued' | 'sent' | 'read' | 'failed';
  reference_type?: string | null;
  reference_id?: string | null;
  created_at: string;
  read_at?: string | null;
}

export interface ServiceEarningsSummary {
  service_name: string;
  bookings_count: number;
  total_collected: number;
  total_earned: number;
}

export interface ServiceBookingRatingItem {
  id: string;
  booking_id: string;
  tenant_id: string;
  service_provider_id: string;
  rating: number;
  review_text?: string | null;
  created_at: string;
}

const VAPID_PUBLIC_KEY = 'BN8GNVmT6nvk2zsnKOgRXXkJnLNQ_b0MNcosVXitGpLsSUfOF9BIm0JSf7sEjLqEQdoT7QQ5mO9_OS_7KZP3HOQ';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function formatRelativeTime(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSec < 60) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export default function ProfessionalDashboard() {
  const navigate = useNavigate();
  const supabase = getSupabaseClient();
  const { canSwitchRoles, setRoleSwitchChoice } = useAuth();

  // Bill print modal state (PATCH #2ad Task D)
  const [showBillPrintModal, setShowBillPrintModal] = useState(false);
  const [billPrintBookingId, setBillPrintBookingId] = useState<string | null>(null);

  // Helper service client for fallback read operations if RLS on certain tables restricts read
  const adminFallback = useMemo(() => {
    const url = import.meta.env.VITE_SUPABASE_URL;
    const sKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;
    if (url && sKey) {
      return createClient(url, sKey);
    }
    return null;
  }, []);

  // Identity state
  const [loading, setLoading] = useState(true);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [professional, setProfessional] = useState<ServiceProfessionalIdentity | null>(null);

  // Active Tab: 'jobs' | 'earnings' | 'ledger' | 'profile'
  const [activeTab, setActiveTab] = useState<'jobs' | 'earnings' | 'ledger' | 'profile'>('jobs');
  const [refreshing, setRefreshing] = useState(false);

  // Notifications State (Part A)
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  // Tab 1: My Jobs state
  const [bookings, setBookings] = useState<ServiceBookingItem[]>([]);
  const [catalogMap, setCatalogMap] = useState<Map<string, string>>(new Map());
  const [jobFilter, setJobFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [selectedBooking, setSelectedBooking] = useState<ServiceBookingItem | null>(null);
  const [selectedJobLines, setSelectedJobLines] = useState<any[]>([]);

  // Load all service lines for selected job in ProfessionalDashboard
  useEffect(() => {
    if (selectedBooking?.id && supabase) {
      supabase.rpc('get_service_booking_lines', { p_booking_id: selectedBooking.id })
        .then(({ data, error }: { data: any; error: any }) => {
          if (!error && Array.isArray(data)) {
            setSelectedJobLines(data);
          } else {
            setSelectedJobLines([]);
          }
        });
    } else {
      setSelectedJobLines([]);
    }
  }, [selectedBooking?.id, supabase]);

  // Action Dialog states in Job Detail
  const [actionLoading, setActionLoading] = useState(false);
  const [showStartOtpModal, setShowStartOtpModal] = useState(false);
  const [startOtpInput, setStartOtpInput] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeOtpInput, setCompleteOtpInput] = useState('');
  const [finalPriceInput, setFinalPriceInput] = useState('');
  const [showCashConfirmModal, setShowCashConfirmModal] = useState(false);
  const [cashCollectAmount, setCashCollectAmount] = useState<string>('');

  // Online Payment Link state
  const [paymentLinkData, setPaymentLinkData] = useState<{
    short_url: string;
    payment_link_id: string;
    amount: number;
  } | null>(null);
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);

  // Tab 2: My Earnings state
  const [wallet, setWallet] = useState<WalletAccount | null>(null);
  const [ledgerTransactions, setLedgerTransactions] = useState<LedgerTransaction[]>([]);
  const [serviceEarnings, setServiceEarnings] = useState<ServiceEarningsSummary[]>([]);
  const [loadingServiceEarnings, setLoadingServiceEarnings] = useState(false);

  // Tab 3: My Profile & Ratings state (Part C)
  const [reviews, setReviews] = useState<ServiceBookingRatingItem[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);

  // Push Notifications state (Part E)
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default');
  const [isIosSafari, setIsIosSafari] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showPushBanner, setShowPushBanner] = useState(true);
  const [enablingPush, setEnablingPush] = useState(false);

  // Close notifications on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotificationPanel(false);
      }
    }
    if (showNotificationPanel) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotificationPanel]);

  // Check Web Push support & iOS platform limitations
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const ua = window.navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua);
    const isSafari = /safari/.test(ua) && !/chrome|crios|firefox|fxios/.test(ua);
    setIsIosSafari(isIos && isSafari);

    const standalone = 
      (window.navigator as any).standalone === true || 
      window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standalone);

    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setPushSupported(supported);

    if ('Notification' in window) {
      setPushPermission(Notification.permission);
    }
  }, []);

  // ==========================================
  // Step 1 - 4: Identity Resolution
  // ==========================================
  const resolveIdentity = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setIdentityError(null);

    try {
      // 1. Get current user via supabase.auth.getUser()
      const { data: { user }, error: userErr } = await supabase.auth.getUser();
      if (userErr || !user) {
        navigate('/professional/login', { replace: true });
        return;
      }

      // 2. Query service_providers where user_id = that user's id (expect exactly one row)
      let sp: any = null;
      const { data: spData, error: spErr } = await supabase
        .from('service_providers')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (spData) {
        sp = spData;
      } else if (adminFallback) {
        const { data: spAdmin } = await adminFallback
          .from('service_providers')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        sp = spAdmin;
      }

      if (!sp) {
        setIdentityError("Your account isn't set up correctly. Please contact your business.");
        setLoading(false);
        return;
      }

      // 3. Query service_professionals where service_provider_id = that service_provider's id
      let pro: any = null;
      const { data: proData, error: proErr } = await supabase
        .from('service_professionals')
        .select('id, full_name, phone, commission_percent, branch_id, tenant_id, service_provider_id')
        .eq('service_provider_id', sp.id)
        .maybeSingle();

      if (proData) {
        pro = proData;
      } else if (adminFallback) {
        const { data: proAdmin } = await adminFallback
          .from('service_professionals')
          .select('id, full_name, phone, commission_percent, branch_id, tenant_id, service_provider_id')
          .eq('service_provider_id', sp.id)
          .maybeSingle();
        pro = proAdmin;
      }

      // 4. If either lookup finds no row, handle error or fallback
      if (!pro) {
        if (sp && sp.provider_type === 'service_professional') {
          pro = {
            id: sp.id,
            service_provider_id: sp.id,
            tenant_id: sp.tenant_id,
            branch_id: null,
            full_name: sp.full_name,
            phone: sp.phone,
            commission_percent: 0,
            rating: sp.rating !== null && sp.rating !== undefined ? Number(sp.rating) : null
          };
        } else {
          setIdentityError("Your account isn't set up correctly. Please contact your business.");
          setLoading(false);
          return;
        }
      }

      setProfessional({
        id: pro.id,
        service_provider_id: sp.id,
        tenant_id: sp.tenant_id || pro.tenant_id,
        branch_id: pro.branch_id,
        full_name: pro.full_name || sp.full_name,
        phone: pro.phone || sp.phone,
        commission_percent: pro.commission_percent ?? 0,
        rating: sp.rating !== null && sp.rating !== undefined ? Number(sp.rating) : null
      });
    } catch (err: any) {
      console.error('Error resolving identity:', err);
      setIdentityError("Your account isn't set up correctly. Please contact your business.");
    } finally {
      setLoading(false);
    }
  }, [supabase, adminFallback, navigate]);

  useEffect(() => {
    resolveIdentity();
  }, [resolveIdentity]);

  // ==========================================
  // Part A: Notifications Polling
  // ==========================================
  const fetchNotifications = useCallback(async () => {
    if (!supabase || !professional) return;
    try {
      const { data, error } = await supabase
        .from('notification_queue')
        .select('*')
        .eq('channel', 'in_app')
        .eq('recipient_type', 'service_provider')
        .in('status', ['queued', 'sent'])
        .order('created_at', { ascending: false })
        .limit(50);

      if (data && !error) {
        setNotifications(data as NotificationItem[]);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  }, [supabase, professional]);

  // Polling every 30 seconds
  useEffect(() => {
    if (!professional) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [professional, fetchNotifications]);

  // Handle single notification tap: mark read & open booking drawer if reference_type === 'service_booking'
  const handleNotificationTap = async (notif: NotificationItem) => {
    if (!supabase) return;

    // 1. Mark notification as read
    try {
      await supabase
        .from('notification_queue')
        .update({ status: 'read', read_at: new Date().toISOString() })
        .eq('id', notif.id);

      setNotifications(prev => prev.filter(n => n.id !== notif.id));
    } catch (err) {
      console.error('Error marking notification read:', err);
    }

    setShowNotificationPanel(false);

    // 2. Open booking detail if service_booking
    if (notif.reference_type === 'service_booking' && notif.reference_id) {
      setActiveTab('jobs');
      const found = bookings.find(b => b.id === notif.reference_id);
      if (found) {
        setSelectedBooking(found);
        setPaymentLinkData(null);
      } else {
        // Fallback fetch if not in currently loaded slice
        const { data: bRow } = await supabase
          .from('service_bookings')
          .select('*')
          .eq('id', notif.reference_id)
          .maybeSingle();

        if (bRow) {
          setSelectedBooking(bRow as ServiceBookingItem);
          setPaymentLinkData(null);
        }
      }
    }
  };

  // Mark all notifications as read
  const handleMarkAllNotificationsRead = async () => {
    if (!supabase || notifications.length === 0) return;
    setMarkingAllRead(true);
    try {
      const ids = notifications.map(n => n.id);
      const { error } = await supabase
        .from('notification_queue')
        .update({ status: 'read', read_at: new Date().toISOString() })
        .in('id', ids);

      if (error) throw error;

      setNotifications([]);
      toast.success('All notifications marked as read');
    } catch (err: any) {
      console.error('Error marking all notifications read:', err);
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAllRead(false);
    }
  };

  // ==========================================
  // Part B: Service-wise Earnings Summary
  // ==========================================
  const fetchServiceEarnings = useCallback(async () => {
    if (!supabase || !professional) return;
    setLoadingServiceEarnings(true);
    try {
      const { data, error } = await supabase.rpc('get_professional_earnings_summary', {
        p_service_provider_id: professional.service_provider_id
      });

      if (error) throw error;
      setServiceEarnings(data || []);
    } catch (err: any) {
      console.error('Error fetching service earnings summary:', err);
    } finally {
      setLoadingServiceEarnings(false);
    }
  }, [supabase, professional]);

  // ==========================================
  // Part C: Profile & Ratings
  // ==========================================
  const fetchReviews = useCallback(async () => {
    if (!supabase || !professional) return;
    setLoadingReviews(true);
    try {
      const { data, error } = await supabase
        .from('service_booking_ratings')
        .select('*')
        .eq('service_provider_id', professional.service_provider_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (err: any) {
      console.error('Error fetching reviews:', err);
    } finally {
      setLoadingReviews(false);
    }
  }, [supabase, professional]);

  // ==========================================
  // Part E: Web Push Subscription
  // ==========================================
  const handleEnablePushAlerts = async () => {
    if (!pushSupported) {
      toast.error('Web Push is not supported by your browser on this device');
      return;
    }

    setEnablingPush(true);
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      const permission = await Notification.requestPermission();
      setPushPermission(permission);

      if (permission !== 'granted') {
        toast.error('Notification permission was not granted. Please enable it in browser settings.');
        return;
      }

      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      const { data: { session } } = await supabase!.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Session token expired. Please log in again.');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://rdioqcpluulhpxnclhfp.supabase.co';
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const res = await fetch(`${supabaseUrl}/functions/v1/save-push-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': anonKey,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(subscription.toJSON())
      });

      const resJson = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(resJson.error || resJson.message || 'Failed to register push subscription on server');
      }

      toast.success('Job alerts enabled on this device');
      setShowPushBanner(false);
    } catch (err: any) {
      console.error('Error enabling push alerts:', err);
      toast.error(err.message || 'Failed to enable push alerts');
    } finally {
      setEnablingPush(false);
    }
  };

  // ==========================================
  // Load All Dashboard Data
  // ==========================================
  const loadDashboardData = useCallback(async () => {
    if (!professional || !supabase) return;
    setRefreshing(true);

    try {
      // 1. Fetch catalog items for service name resolution
      let catItems: any[] = [];
      const { data: catData } = await supabase
        .from('service_catalog_items')
        .select('id, service_name, base_price')
        .eq('tenant_id', professional.tenant_id);

      if (catData && catData.length > 0) {
        catItems = catData;
      } else if (adminFallback) {
        const { data: catAdmin } = await adminFallback
          .from('service_catalog_items')
          .select('id, service_name, base_price')
          .eq('tenant_id', professional.tenant_id);
        catItems = catAdmin || [];
      }

      const cMap = new Map<string, string>();
      catItems.forEach((c: any) => cMap.set(c.id, c.service_name));
      setCatalogMap(cMap);

      // 2. Fetch service_bookings for this professional (newest created_at first)
      let bItems: ServiceBookingItem[] = [];
      const { data: bData } = await supabase
        .from('service_bookings')
        .select('*')
        .eq('professional_id', professional.id)
        .order('created_at', { ascending: false });

      if (bData && bData.length > 0) {
        bItems = bData as ServiceBookingItem[];
      } else if (adminFallback) {
        const { data: bAdmin } = await adminFallback
          .from('service_bookings')
          .select('*')
          .eq('professional_id', professional.id)
          .order('created_at', { ascending: false });
        bItems = (bAdmin as ServiceBookingItem[]) || [];
      }

      setBookings(bItems);

      // If a booking is currently opened in detail, update its latest state
      setSelectedBooking(prev => {
        if (!prev) return null;
        const updated = bItems.find(b => b.id === prev.id);
        return updated || null;
      });

      // 3. Fetch Wallet Account
      let wData: any = null;
      const { data: clientW } = await supabase
        .from('wallet_accounts')
        .select('*')
        .eq('owner_type', 'staff')
        .eq('owner_id', professional.service_provider_id)
        .maybeSingle();

      if (clientW) {
        wData = clientW;
      } else if (adminFallback) {
        const { data: adminW } = await adminFallback
          .from('wallet_accounts')
          .select('*')
          .eq('owner_type', 'staff')
          .eq('owner_id', professional.service_provider_id)
          .maybeSingle();
        wData = adminW;
      }

      setWallet(wData);

      // 4. Fetch Ledger Transactions
      if (wData?.id) {
        let lItems: LedgerTransaction[] = [];
        const { data: clientL } = await supabase
          .from('ledger_transactions')
          .select('*')
          .eq('wallet_account_id', wData.id)
          .order('created_at', { ascending: false });

        if (clientL && clientL.length > 0) {
          lItems = clientL as LedgerTransaction[];
        } else if (adminFallback) {
          const { data: adminL } = await adminFallback
            .from('ledger_transactions')
            .select('*')
            .eq('wallet_account_id', wData.id)
            .order('created_at', { ascending: false });
          lItems = (adminL as LedgerTransaction[]) || [];
        }

        setLedgerTransactions(lItems);
      }

      // 5. Fetch Service-wise Earnings and Reviews
      fetchServiceEarnings();
      fetchReviews();
      fetchNotifications();
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      toast.error('Failed to refresh data: ' + (err.message || 'Unknown error'));
    } finally {
      setRefreshing(false);
    }
  }, [professional, supabase, adminFallback, fetchServiceEarnings, fetchReviews, fetchNotifications]);

  useEffect(() => {
    if (professional) {
      loadDashboardData();
    }
  }, [professional, loadDashboardData]);

  // Handle Logout
  const handleLogout = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    toast.success('Signed out successfully');
    navigate('/professional/login', { replace: true });
  };

  // ==========================================
  // Action Handlers for Bookings
  // ==========================================

  // 1. Accept or Reject Booking
  const handleAcceptOrReject = async (newStatus: 'accepted' | 'rejected') => {
    if (!selectedBooking || !supabase) return;
    setActionLoading(true);

    try {
      const { error } = await supabase.rpc('update_service_booking_status', {
        p_booking_id: selectedBooking.id,
        p_new_status: newStatus,
        p_otp: null,
        p_cancelled_reason: null,
        p_final_price: null
      });

      if (error) throw error;

      toast.success(newStatus === 'accepted' ? 'Job accepted! You can now travel to customer.' : 'Job rejected.');
      await loadDashboardData();
    } catch (err: any) {
      console.error(`Error transitioning to ${newStatus}:`, err);
      toast.error(err.message || `Failed to mark job as ${newStatus}`);
    } finally {
      setActionLoading(false);
    }
  };

  // 2. Start Service (Requires customer's Start OTP)
  const handleStartServiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBooking || !supabase) return;

    if (!startOtpInput.trim()) {
      toast.error('Please enter the 4-digit start OTP provided by the customer');
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('update_service_booking_status', {
        p_booking_id: selectedBooking.id,
        p_new_status: 'in_progress',
        p_otp: startOtpInput.trim(),
        p_cancelled_reason: null,
        p_final_price: null
      });

      if (error) throw error;

      toast.success('Service started successfully!');
      setShowStartOtpModal(false);
      setStartOtpInput('');
      await loadDashboardData();
    } catch (err: any) {
      console.error('Start service error:', err);
      toast.error(err.message || 'Incorrect Start OTP. Please ask the customer again.');
    } finally {
      setActionLoading(false);
    }
  };

  // 3. Complete Service (Requires customer's Completion OTP & optional Final Price)
  const handleCompleteServiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBooking || !supabase) return;

    if (!completeOtpInput.trim()) {
      toast.error('Please enter the completion OTP provided by the customer');
      return;
    }

    const priceNum = finalPriceInput ? Number(finalPriceInput) : selectedBooking.quoted_price;

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('update_service_booking_status', {
        p_booking_id: selectedBooking.id,
        p_new_status: 'completed',
        p_otp: completeOtpInput.trim(),
        p_cancelled_reason: null,
        p_final_price: priceNum
      });

      if (error) throw error;

      toast.success('Service marked as completed!');
      setShowCompleteModal(false);
      setCompleteOtpInput('');
      setFinalPriceInput('');
      await loadDashboardData();

      // Show auto-generated bill with QR and Share PDF (PATCH #2ad Task D)
      setBillPrintBookingId(selectedBooking.id);
      setShowBillPrintModal(true);
    } catch (err: any) {
      console.error('Complete service error:', err);
      toast.error(err.message || 'Incorrect Completion OTP. Please ask the customer again.');
    } finally {
      setActionLoading(false);
    }
  };

  // 4. Collect Cash Payment
  const handleCollectCash = async () => {
    if (!selectedBooking || !supabase) return;

    const amountToCollect = selectedBooking.invoice_id
      ? (selectedBooking.final_price || selectedBooking.quoted_price)
      : (cashCollectAmount ? Number(cashCollectAmount) : (selectedBooking.final_price || selectedBooking.quoted_price));
    setActionLoading(true);

    try {
      const { error } = await supabase.rpc('collect_booking_cash_payment', {
        p_booking_id: selectedBooking.id,
        p_amount: amountToCollect
      });

      if (error) throw error;

      toast.success(`Collected ₹${amountToCollect} in cash!`);
      setShowCashConfirmModal(false);
      await loadDashboardData();
    } catch (err: any) {
      console.error('Collect cash error:', err);
      toast.error(err.message || 'Failed to record cash payment');
    } finally {
      setActionLoading(false);
    }
  };

  // 5. Request Online Payment via Edge Function
  const handleRequestOnlinePayment = async () => {
    if (!selectedBooking || !supabase) return;
    setGeneratingPaymentLink(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Session token not found. Please log in again.');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://rdioqcpluulhpxnclhfp.supabase.co';
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const res = await fetch(`${supabaseUrl}/functions/v1/create-service-payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': anonKey,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          booking_id: selectedBooking.id
        })
      });

      const resJson = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(resJson.error || resJson.message || `Failed with status ${res.status}`);
      }

      setPaymentLinkData({
        short_url: resJson.short_url,
        payment_link_id: resJson.payment_link_id,
        amount: resJson.amount || selectedBooking.final_price || selectedBooking.quoted_price
      });

      toast.success('Payment link generated! Show QR or share link with customer.');
      await loadDashboardData();
    } catch (err: any) {
      console.error('Error generating payment link:', err);
      toast.error(err.message || 'Failed to create payment link');
    } finally {
      setGeneratingPaymentLink(false);
    }
  };

  // Filtered Bookings
  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      if (jobFilter === 'active') {
        return ['assigned', 'accepted', 'in_progress'].includes(b.status);
      }
      if (jobFilter === 'completed') {
        return b.status === 'completed';
      }
      return true;
    });
  }, [bookings, jobFilter]);

  // Average Rating computation
  const averageRatingDisplay = useMemo(() => {
    if (professional?.rating !== null && professional?.rating !== undefined && Number(professional.rating) > 0) {
      return Number(professional.rating).toFixed(1);
    }
    if (reviews.length > 0) {
      const avg = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
      return avg.toFixed(1);
    }
    return null;
  }, [professional?.rating, reviews]);

  // Badge helpers
  const getStatusBadge = (status: BookingStatus) => {
    switch (status) {
      case 'assigned':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
            Assigned
          </span>
        );
      case 'accepted':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
            Accepted
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800 animate-pulse">
            In Progress
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
            Cancelled
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-300 dark:border-slate-700">
            Rejected
          </span>
        );
      case 'no_show':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300 border border-orange-200 dark:border-orange-800">
            No Show
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
            {status}
          </span>
        );
    }
  };

  const getPaymentBadge = (booking: ServiceBookingItem) => {
    if (booking.payment_status === 'paid') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
          <Check size={11} />
          <span>Paid {booking.payment_mode ? `(${booking.payment_mode})` : ''}</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
        Payment Pending
      </span>
    );
  };

  // ----------------------------------------------------
  // Error state if account isn't set up correctly
  // ----------------------------------------------------
  if (!loading && identityError) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700 text-white rounded-2xl shadow-2xl p-6 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-rose-500/20 text-rose-400 flex items-center justify-center">
            <ShieldAlert size={32} />
          </div>
          <h2 className="text-xl font-bold mb-2">Account Configuration Error</h2>
          <p className="text-sm text-slate-300 mb-6 leading-relaxed">
            {identityError}
          </p>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-slate-600 text-white hover:bg-slate-700 text-sm h-11 rounded-xl"
          >
            <LogOut size={16} className="mr-2" />
            <span>Sign Out</span>
          </Button>
        </Card>
      </div>
    );
  }

  // ----------------------------------------------------
  // Loading state
  // ----------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 text-slate-400">
        <Loader2 size={36} className="animate-spin text-primary mb-3" />
        <p className="text-sm font-medium">Loading partner profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col pb-20 sm:pb-8">
      {/* Top Navbar with Notification Bell (Part A) */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-md border-b border-slate-800 px-4 py-3 sm:px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/20 text-primary flex items-center justify-center font-bold text-base border border-primary/30">
              {professional?.full_name?.charAt(0)?.toUpperCase() || 'P'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-bold text-white truncate max-w-[150px] sm:max-w-xs">
                  {professional?.full_name}
                </h1>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  Partner
                </span>
              </div>
              <p className="text-[11px] text-slate-400 flex items-center gap-2">
                {professional?.commission_percent === 0 ? (
                  <span className="text-emerald-400 font-semibold">Owner (0% Commission)</span>
                ) : (
                  <span>Commission: <strong className="text-slate-200">{professional?.commission_percent}%</strong></span>
                )}
                {averageRatingDisplay && (
                  <span className="text-amber-300 font-semibold flex items-center gap-0.5">
                    <Star size={11} className="fill-amber-400 text-amber-400" />
                    {averageRatingDisplay}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Role switch (PATCH #2ad Task B): shown if user has BOTH tenant role AND service_providers row */}
            {canSwitchRoles && (
              <div className="inline-flex items-center rounded-xl bg-slate-800 p-1 border border-slate-700 text-xs font-semibold mr-1">
                <button
                  type="button"
                  onClick={() => {
                    setRoleSwitchChoice('merchant');
                    navigate('/merchant-dashboard');
                  }}
                  className="px-2.5 py-1.5 rounded-lg text-slate-400 hover:text-white transition-all flex items-center gap-1.5"
                >
                  <Briefcase size={12} />
                  <span>Merchant Dashboard</span>
                </button>
                <button
                  type="button"
                  className="px-2.5 py-1.5 rounded-lg bg-slate-900 text-primary shadow-sm font-bold transition-all cursor-default"
                >
                  My Jobs (Professional App)
                </button>
              </div>
            )}

            {/* Notification Bell (Part A) */}
            <div className="relative" ref={notifRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNotificationPanel(!showNotificationPanel)}
                className={`relative h-9 w-9 p-0 rounded-xl transition-colors ${
                  showNotificationPanel ? 'bg-slate-800 text-white' : 'text-slate-300 hover:text-white hover:bg-slate-800'
                }`}
                title="Notifications"
              >
                <Bell size={18} />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] px-1 items-center justify-center rounded-full bg-rose-500 text-[10px] font-extrabold text-white animate-bounce shadow-md">
                    {notifications.length > 99 ? '99+' : notifications.length}
                  </span>
                )}
              </Button>

              {/* Notification Dropdown Panel */}
              {showNotificationPanel && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-150">
                  <div className="p-3.5 border-b border-slate-700/80 flex items-center justify-between bg-slate-850">
                    <div className="flex items-center gap-2">
                      <Bell size={16} className="text-primary" />
                      <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                        Job Notifications
                      </h3>
                      {notifications.length > 0 && (
                        <span className="px-1.5 py-0.2 rounded-full bg-rose-500/20 text-rose-300 text-[10px] font-bold">
                          {notifications.length} new
                        </span>
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <button
                        onClick={handleMarkAllNotificationsRead}
                        disabled={markingAllRead}
                        className="text-[11px] text-primary hover:text-primary/80 font-medium flex items-center gap-1 disabled:opacity-50"
                      >
                        {markingAllRead ? <Loader2 size={12} className="animate-spin" /> : <CheckCheck size={12} />}
                        <span>Mark all read</span>
                      </button>
                    )}
                  </div>

                  <div className="max-h-80 overflow-y-auto divide-y divide-slate-700/50">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-slate-400">
                        <CheckCircle2 size={24} className="mx-auto mb-2 text-slate-500" />
                        <p className="text-xs font-medium">No new notifications</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">You're all caught up with your job alerts.</p>
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div
                          key={notif.id}
                          onClick={() => handleNotificationTap(notif)}
                          className="p-3.5 hover:bg-slate-700/40 cursor-pointer transition-colors relative flex items-start gap-2.5 group"
                        >
                          <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-slate-100 font-medium leading-relaxed group-hover:text-primary transition-colors">
                              {notif.rendered_body}
                            </p>
                            <span className="text-[10px] text-slate-400 mt-1 block">
                              {formatRelativeTime(notif.created_at)}
                            </span>
                          </div>
                          {notif.reference_type === 'service_booking' && (
                            <ChevronRight size={14} className="text-slate-500 group-hover:text-primary group-hover:translate-x-0.5 transition-transform mt-1 shrink-0" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={loadDashboardData}
              disabled={refreshing}
              className="h-9 w-9 p-0 text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl"
              title="Refresh"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin text-primary' : ''} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="h-9 w-9 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-950/40 rounded-xl"
              title="Sign Out"
            >
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Tabs Header (Part C adds "My Profile") */}
      <div className="bg-slate-800/80 border-b border-slate-800 px-4 py-2 sticky top-[57px] z-30 backdrop-blur-md">
        <div className="max-w-3xl mx-auto flex gap-2">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'jobs'
                ? 'bg-primary text-white shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <Briefcase size={15} />
            <span>My Jobs</span>
            {bookings.length > 0 && (
              <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                activeTab === 'jobs' ? 'bg-white/20 text-white' : 'bg-slate-700 text-slate-300'
              }`}>
                {bookings.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('earnings')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'earnings'
                ? 'bg-primary text-white shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <Wallet size={15} />
            <span>My Earnings</span>
            {wallet && (
              <span className={`text-[11px] font-bold ${
                (wallet.current_balance ?? 0) < 0 ? 'text-amber-300' : 'text-emerald-300'
              }`}>
                ₹{Math.abs(wallet.current_balance ?? 0).toFixed(0)}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('ledger')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'ledger'
                ? 'bg-primary text-white shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <Receipt size={15} />
            <span>My Ledger</span>
          </button>

          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'profile'
                ? 'bg-primary text-white shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <User size={15} />
            <span>My Profile</span>
            {averageRatingDisplay && (
              <span className="text-[11px] font-bold text-amber-300 flex items-center gap-0.5">
                <Star size={11} className="fill-amber-400" />
                {averageRatingDisplay}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Dismissible Web Push Alert Banner (Part E) */}
      {showPushBanner && pushPermission !== 'granted' && (
        <div className="bg-gradient-to-r from-indigo-900/90 via-primary/80 to-purple-900/90 border-b border-indigo-700/50 px-4 py-3 text-xs shadow-md">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
            <div className="flex items-start gap-2.5">
              <div className="p-1.5 rounded-lg bg-white/20 text-white shrink-0 mt-0.5">
                <Smartphone size={16} />
              </div>
              <div>
                <p className="font-bold text-white">Enable Real-Time Job Alerts on This Phone</p>
                {isIosSafari && !isStandalone ? (
                  <p className="text-[11px] text-indigo-200 mt-0.5 leading-relaxed">
                    On iPhone, first tap <strong>Share</strong> &rarr; <strong>Add to Home Screen</strong>, then open BahiBox from your home screen to enable alerts.
                  </p>
                ) : (
                  <p className="text-[11px] text-indigo-200 mt-0.5">
                    Receive instant push notifications on your phone lock screen whenever a new booking is assigned.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
              {!(isIosSafari && !isStandalone) && (
                <Button
                  size="sm"
                  onClick={handleEnablePushAlerts}
                  disabled={enablingPush}
                  className="bg-white text-indigo-950 hover:bg-slate-100 font-bold text-xs h-8 px-3 rounded-lg shadow"
                >
                  {enablingPush ? (
                    <div className="flex items-center gap-1.5">
                      <Loader2 size={12} className="animate-spin" />
                      <span>Enabling...</span>
                    </div>
                  ) : (
                    'Enable Alerts'
                  )}
                </Button>
              )}
              <button
                onClick={() => setShowPushBanner(false)}
                className="p-1 rounded-lg text-indigo-200 hover:text-white hover:bg-white/10"
              >
                <X size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content Container */}
      <main className="flex-1 max-w-3xl w-full mx-auto p-4 sm:p-6">
        {/* ======================================================== */}
        {/* TAB 1: MY JOBS                                           */}
        {/* ======================================================== */}
        {activeTab === 'jobs' && (
          <div className="space-y-4">
            {/* Filter Pills */}
            <div className="flex items-center gap-2 pb-1 overflow-x-auto">
              <button
                onClick={() => setJobFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                  jobFilter === 'all'
                    ? 'bg-slate-100 text-slate-900 font-bold'
                    : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                All Jobs ({bookings.length})
              </button>
              <button
                onClick={() => setJobFilter('active')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                  jobFilter === 'active'
                    ? 'bg-blue-600 text-white font-bold'
                    : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                Active / In Progress (
                {bookings.filter(b => ['assigned', 'accepted', 'in_progress'].includes(b.status)).length}
                )
              </button>
              <button
                onClick={() => setJobFilter('completed')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                  jobFilter === 'completed'
                    ? 'bg-emerald-600 text-white font-bold'
                    : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                Completed ({bookings.filter(b => b.status === 'completed').length})
              </button>
            </div>

            {/* Bookings List */}
            {filteredBookings.length === 0 ? (
              <Card className="bg-slate-800/50 border-dashed border-slate-700 text-center p-8 rounded-2xl">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-800 flex items-center justify-center text-slate-500">
                  <Briefcase size={22} />
                </div>
                <h3 className="text-sm font-bold text-slate-200 mb-1">No Jobs Found</h3>
                <p className="text-xs text-slate-400">
                  {jobFilter === 'all'
                    ? 'You have not been assigned any service jobs yet.'
                    : 'No jobs match the selected filter.'}
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredBookings.map(b => {
                  const serviceName = catalogMap.get(b.catalog_item_id) || b.service_catalog_items?.service_name || 'Service Booking';
                  const formattedSchedule = b.scheduled_at
                    ? new Date(b.scheduled_at).toLocaleString([], {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'On-demand';

                  return (
                    <div
                      key={b.id}
                      onClick={() => {
                        setSelectedBooking(b);
                        setPaymentLinkData(null);
                      }}
                      className="bg-slate-800/90 hover:bg-slate-800 border border-slate-700/80 hover:border-slate-600 rounded-2xl p-4 transition-all cursor-pointer shadow-sm relative group"
                    >
                      {/* Top Bar: Booking #, Badges */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-bold text-primary tracking-wide">
                              {b.booking_number}
                            </span>
                            {getStatusBadge(b.status)}
                          </div>
                          <h3 className="text-sm font-bold text-white mt-1">
                            {serviceName}
                            {b.quantity && b.quantity > 1 ? (
                              <span className="ml-1 text-xs text-primary font-bold">x {b.quantity}</span>
                            ) : null}
                          </h3>
                          {b.customer_notes && (
                            <p className="text-[11px] text-slate-400 italic line-clamp-1 mt-0.5">
                              Note: {b.customer_notes}
                            </p>
                          )}
                        </div>

                        <div className="text-right">
                          <span className="text-base font-bold text-white flex items-center justify-end">
                            <IndianRupee size={14} className="text-slate-400" />
                            {b.final_price || b.quoted_price}
                          </span>
                          <div className="mt-1">
                            {getPaymentBadge(b)}
                          </div>
                        </div>
                      </div>

                      {/* Customer Info */}
                      <div className="space-y-1 text-xs text-slate-300 mb-3 pt-2 border-t border-slate-700/50">
                        <div className="flex items-center gap-1.5 font-medium text-slate-200">
                          <User size={13} className="text-slate-400 shrink-0" />
                          <span>{b.customer_name}</span>
                          {b.customer_phone && (
                            <span className="text-slate-400">({b.customer_phone})</span>
                          )}
                        </div>

                        {b.customer_address && (
                          <div className="flex items-start gap-1.5 text-slate-400 line-clamp-1">
                            <MapPin size={13} className="text-slate-500 shrink-0 mt-0.5" />
                            <span className="truncate">{b.customer_address}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                          <Clock size={12} className="text-slate-500 shrink-0" />
                          <span>Scheduled: {formattedSchedule}</span>
                        </div>
                      </div>

                      {/* Bottom Hint */}
                      <div className="flex items-center justify-between text-[11px] text-slate-400 pt-2 border-t border-slate-700/40">
                        <span className="text-slate-500">
                          Created {new Date(b.created_at).toLocaleDateString()}
                        </span>
                        <span className="text-primary font-semibold flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform">
                          <span>View Job Details & Actions</span>
                          <ChevronRight size={13} />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ======================================================== */}
        {/* TAB 2: MY EARNINGS                                       */}
        {/* ======================================================== */}
        {activeTab === 'earnings' && (
          <div className="space-y-6">
            {/* Wallet Balance Card (Preserved untouched) */}
            <Card className="bg-gradient-to-br from-slate-800 to-slate-850 border-slate-700 rounded-2xl shadow-lg overflow-hidden">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Wallet Account Balance
                    </span>
                    <div className="text-3xl font-extrabold text-white mt-1 flex items-center">
                      <IndianRupee size={24} className="text-slate-400 mr-0.5" />
                      {Math.abs(wallet?.current_balance ?? 0).toFixed(2)}
                    </div>
                  </div>

                  <div className={`p-3 rounded-2xl ${
                    (wallet?.current_balance ?? 0) < 0
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  }`}>
                    {(wallet?.current_balance ?? 0) < 0 ? (
                      <TrendingDown size={24} />
                    ) : (
                      <TrendingUp size={24} />
                    )}
                  </div>
                </div>

                {/* Explanation Banner */}
                <div className="mt-4 p-3 rounded-xl border text-xs leading-relaxed">
                  {professional?.commission_percent === 0 ? (
                    <div className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 p-2.5 rounded-lg flex items-start gap-2">
                      <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-emerald-400" />
                      <div>
                        <strong>Owner Profile (Direct Merchant Operations)</strong>
                        <p className="mt-0.5 text-emerald-200/80">
                          100% of customer collections belong directly to your business. No staff commission is deducted or payable.
                        </p>
                      </div>
                    </div>
                  ) : (wallet?.current_balance ?? 0) < 0 ? (
                    <div className="bg-amber-500/10 border-amber-500/30 text-amber-300 p-2.5 rounded-lg flex items-start gap-2">
                      <AlertCircle size={16} className="shrink-0 mt-0.5 text-amber-400" />
                      <div>
                        <strong>You owe the business ₹{Math.abs(wallet?.current_balance ?? 0).toFixed(2)}.</strong>
                        <p className="mt-0.5 text-amber-200/80">
                          This happens when you collect full cash from customers and business commission is deducted from your wallet ledger.
                        </p>
                      </div>
                    </div>
                  ) : (wallet?.current_balance ?? 0) > 0 ? (
                    <div className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 p-2.5 rounded-lg flex items-start gap-2">
                      <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-emerald-400" />
                      <div>
                        <strong>Payable to you: ₹{(wallet?.current_balance ?? 0).toFixed(2)}.</strong>
                        <p className="mt-0.5 text-emerald-200/80">
                          Your net commission earnings from digital payments ready for payout.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-700/50 text-slate-300 p-2.5 rounded-lg flex items-center gap-2">
                      <Check size={16} className="text-emerald-400" />
                      <span>All accounts settled. Zero outstanding balance.</span>
                    </div>
                  )}
                </div>

                {/* Rate details */}
                <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-700/60 text-xs">
                  <div>
                    <span className="text-slate-400 block text-[11px]">Agreed Commission Rate</span>
                    <span className="font-bold text-white flex items-center gap-1 mt-0.5">
                      <Percent size={13} className="text-primary" />
                      {professional?.commission_percent === 0 ? '0% (Owner - Direct)' : `${professional?.commission_percent}% per completed job`}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Wallet Status</span>
                    <span className="font-bold text-emerald-400 flex items-center gap-1 mt-0.5">
                      <CheckCircle2 size={13} />
                      {professional?.commission_percent === 0 ? 'Merchant Direct' : 'Active Staff Wallet'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Transactions History (Preserved untouched) */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-white flex items-center gap-2">
                  <Receipt size={16} className="text-primary" />
                  <span>Recent Ledger Transactions</span>
                </h2>
                <span className="text-xs text-slate-400">
                  {ledgerTransactions.length} records
                </span>
              </div>

              {ledgerTransactions.length === 0 ? (
                <Card className="bg-slate-800/40 border-slate-700 text-center p-6 rounded-2xl">
                  <p className="text-xs text-slate-400">No transactions recorded yet in your wallet.</p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {ledgerTransactions.map(tx => {
                    const isCredit = tx.transaction_type === 'credit';
                    return (
                      <div
                        key={tx.id}
                        className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-3 flex items-center justify-between text-xs"
                      >
                        <div className="min-w-0 pr-2">
                          <p className="font-semibold text-white truncate">
                            {tx.description || (isCredit ? 'Credit' : 'Debit')}
                          </p>
                          <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                            <span>{new Date(tx.created_at).toLocaleString()}</span>
                            {tx.reference_type && (
                              <span className="px-1.5 py-0.2 rounded bg-slate-700 text-slate-300 font-mono text-[10px]">
                                {tx.reference_type}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <span className={`text-sm font-bold flex items-center justify-end ${
                            isCredit ? 'text-emerald-400' : 'text-amber-400'
                          }`}>
                            {isCredit ? '+' : '-'}₹{tx.amount}
                          </span>
                          {tx.balance_after !== null && tx.balance_after !== undefined && (
                            <span className="text-[10px] text-slate-400 block mt-0.5 font-mono">
                              bal: ₹{tx.balance_after}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* PART B: Earnings by Service Breakdown (Below wallet & ledger) */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-white flex items-center gap-2">
                  <Briefcase size={16} className="text-primary" />
                  <span>Earnings by Service</span>
                </h2>
                {serviceEarnings.length > 0 && (
                  <span className="text-xs text-slate-400">
                    {serviceEarnings.length} service type{serviceEarnings.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {loadingServiceEarnings ? (
                <div className="p-6 text-center text-slate-400">
                  <Loader2 size={24} className="animate-spin text-primary mx-auto mb-2" />
                  <p className="text-xs">Loading service earnings breakdown...</p>
                </div>
              ) : serviceEarnings.length === 0 ? (
                <Card className="bg-slate-800/40 border-slate-700 text-center p-6 rounded-2xl">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-slate-800 flex items-center justify-center text-slate-500">
                    <Info size={18} />
                  </div>
                  <p className="text-xs text-slate-300 font-medium">
                    Complete and get paid for a job to see your earnings breakdown here
                  </p>
                </Card>
              ) : (
                <Card className="bg-slate-800/90 border-slate-700 rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-850 text-slate-400 uppercase text-[10px] tracking-wider border-b border-slate-700">
                        <tr>
                          <th className="py-3 px-4">Service Name</th>
                          <th className="py-3 px-3 text-center">Jobs Completed</th>
                          <th className="py-3 px-3 text-right">Total Collected</th>
                          <th className="py-3 px-4 text-right">You Earned</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/60">
                        {serviceEarnings.map((se, idx) => (
                          <tr key={idx} className="hover:bg-slate-700/30 transition-colors">
                            <td className="py-3 px-4 font-semibold text-white">
                              {se.service_name}
                            </td>
                            <td className="py-3 px-3 text-center text-slate-300 font-mono">
                              {se.bookings_count}
                            </td>
                            <td className="py-3 px-3 text-right text-slate-300 font-mono">
                              ₹{Number(se.total_collected).toFixed(2)}
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-emerald-400 font-mono">
                              ₹{Number(se.total_earned).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* TAB: MY LEDGER (Task E - Read Only)                       */}
        {/* ======================================================== */}
        {activeTab === 'ledger' && professional && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-6 shadow-sm">
            <ProfessionalLedgerDrawer
              isOpen={true}
              onClose={() => {}}
              professionalId={professional.id}
              professionalName={professional.full_name}
              readOnly={true}
              inline={true}
            />
          </div>
        )}

        {/* ======================================================== */}
        {/* TAB 3: MY PROFILE & RATINGS (Part C)                      */}
        {/* ======================================================== */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Professional Profile Card */}
            <Card className="bg-gradient-to-br from-slate-800 to-slate-850 border-slate-700 rounded-2xl shadow-lg p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-primary/20 text-primary flex items-center justify-center font-bold text-2xl border border-primary/30 shadow-inner">
                    {professional?.full_name?.charAt(0)?.toUpperCase() || 'P'}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">
                      {professional?.full_name}
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
                      <Phone size={12} className="text-slate-500" />
                      <span>{professional?.phone || 'No phone registered'}</span>
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/20 text-primary border border-primary/30">
                        <Percent size={11} />
                        <span>{professional?.commission_percent === 0 ? 'Owner (Direct - 0% Commission)' : `${professional?.commission_percent}% Commission`}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Rating Card Header */}
                <div className="bg-slate-900/80 border border-slate-700/80 p-4 rounded-2xl text-center sm:text-right shrink-0">
                  <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                    Customer Rating
                  </span>
                  <div className="flex items-center justify-center sm:justify-end gap-1.5 mt-1">
                    <Star size={20} className="fill-amber-400 text-amber-400" />
                    <span className="text-2xl font-black text-white">
                      {averageRatingDisplay || '—'}
                    </span>
                    <span className="text-xs text-slate-400">/ 5</span>
                  </div>
                  <span className="text-[11px] text-slate-400 block mt-0.5">
                    (based on {reviews.length} review{reviews.length === 1 ? '' : 's'})
                  </span>
                </div>
              </div>
            </Card>

            {/* Customer Reviews Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <Star size={16} className="text-amber-400 fill-amber-400" />
                  <span>Customer Reviews</span>
                </h3>
                <span className="text-xs text-slate-400">
                  {reviews.length} feedback{reviews.length === 1 ? '' : 's'}
                </span>
              </div>

              {loadingReviews ? (
                <div className="p-8 text-center text-slate-400">
                  <Loader2 size={24} className="animate-spin text-primary mx-auto mb-2" />
                  <p className="text-xs">Loading verified customer reviews...</p>
                </div>
              ) : reviews.length === 0 ? (
                <Card className="bg-slate-800/40 border-slate-700 text-center p-8 rounded-2xl">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-800 flex items-center justify-center text-amber-400">
                    <Star size={22} />
                  </div>
                  <h4 className="text-sm font-bold text-slate-200 mb-1">No ratings yet</h4>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto">
                    Your rating will appear here after your first completed job is reviewed by the customer.
                  </p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {reviews.map(review => (
                    <div
                      key={review.id}
                      className="bg-slate-800/80 border border-slate-700/70 rounded-2xl p-4 space-y-2 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star
                              key={star}
                              size={15}
                              className={
                                star <= review.rating
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-slate-600'
                              }
                            />
                          ))}
                          <span className="text-xs font-bold text-white ml-1">
                            {review.rating}.0
                          </span>
                        </div>
                        <span className="text-[11px] text-slate-400">
                          {new Date(review.created_at).toLocaleDateString([], {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>

                      {review.review_text ? (
                        <p className="text-xs text-slate-200 leading-relaxed italic bg-slate-900/40 p-2.5 rounded-xl border border-slate-700/40">
                          "{review.review_text}"
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-500 italic">
                          No written comments provided.
                        </p>
                      )}

                      <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1">
                        <span className="flex items-center gap-1 text-emerald-400 font-medium">
                          <CheckCircle2 size={11} />
                          <span>Verified Customer Review</span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* ======================================================== */}
      {/* JOB DETAIL DRAWER / MODAL                                */}
      {/* ======================================================== */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-slate-900 border border-slate-700 w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl shadow-2xl p-5 sm:p-6 animate-in slide-in-from-bottom sm:zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-start justify-between pb-3 border-b border-slate-800 mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-primary">
                    {selectedBooking.booking_number}
                  </span>
                  {getStatusBadge(selectedBooking.status)}
                </div>
                <h2 className="text-base font-bold text-white mt-1">
                  {catalogMap.get(selectedBooking.catalog_item_id) || selectedBooking.service_catalog_items?.service_name || 'Service Job'}
                  {selectedBooking.quantity && selectedBooking.quantity > 1 ? (
                    <span className="ml-1 text-xs text-primary font-bold">x {selectedBooking.quantity}</span>
                  ) : null}
                </h2>
              </div>
              <button
                onClick={() => setSelectedBooking(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            {/* Job Details */}
            <div className="space-y-3.5 text-xs text-slate-300">
              {/* All Service Lines Breakdown if multi-service or single line */}
              {selectedJobLines.length > 0 && (
                <div className="p-3.5 rounded-xl bg-slate-800/80 border border-slate-700/80 space-y-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Booked Services ({selectedJobLines.length})
                  </span>
                  <div className="space-y-1.5 divide-y divide-slate-700/50">
                    {selectedJobLines.map((line: any, idx: number) => (
                      <div key={line.id || idx} className="pt-1.5 first:pt-0 flex items-start justify-between">
                        <div>
                          <div className="font-semibold text-white">
                            {line.item_name || 'Service Item'}
                            <span className="text-primary font-bold ml-1.5">x {line.quantity}</span>
                          </div>
                          {line.notes && (
                            <p className="text-[11px] text-slate-400 italic mt-0.5">{line.notes}</p>
                          )}
                        </div>
                        <span className="font-mono font-bold text-slate-200">
                          ₹{Number(line.line_total || 0).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Work Details / Notes Box if present */}
              {selectedBooking.customer_notes && (
                <div className="p-3.5 rounded-xl bg-slate-800/80 border border-slate-700/80 space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Work Details & Customer Notes
                  </span>
                  <p className="text-white text-xs leading-relaxed font-medium">
                    {selectedBooking.customer_notes}
                  </p>
                </div>
              )}
              {/* Customer Box */}
              <div className="p-3.5 rounded-xl bg-slate-800/80 border border-slate-700/80 space-y-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                  Customer Information
                </span>
                <div className="font-bold text-sm text-white flex items-center justify-between">
                  <span>{selectedBooking.customer_name}</span>
                  {selectedBooking.customer_phone && (
                    <a
                      href={`tel:${selectedBooking.customer_phone}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30 text-xs font-semibold"
                    >
                      <Phone size={13} />
                      <span>Call Customer</span>
                    </a>
                  )}
                </div>

                {selectedBooking.customer_address && (
                  <div className="pt-1.5 border-t border-slate-700/50">
                    <span className="text-[11px] text-slate-400 block mb-0.5">Service Address:</span>
                    <p className="text-slate-200 leading-relaxed">{selectedBooking.customer_address}</p>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedBooking.customer_address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline text-[11px] mt-1 font-medium"
                    >
                      <MapPin size={11} />
                      <span>Open in Google Maps</span>
                      <ExternalLink size={10} />
                    </a>
                  </div>
                )}
              </div>

              {/* Schedule & Price Box */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700/60">
                  <span className="text-[10px] uppercase font-bold text-slate-400 block">Scheduled Time</span>
                  <span className="font-semibold text-white block mt-1">
                    {selectedBooking.scheduled_at
                      ? new Date(selectedBooking.scheduled_at).toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'On-demand'}
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700/60">
                  <span className="text-[10px] uppercase font-bold text-slate-400 block">Price</span>
                  <span className="font-bold text-base text-white block mt-1 flex items-center">
                    <IndianRupee size={14} className="text-slate-400" />
                    {selectedBooking.final_price || selectedBooking.quoted_price}
                  </span>
                  <div className="mt-1">{getPaymentBadge(selectedBooking)}</div>
                </div>
              </div>

              {/* Job Kit for Professional (Equipment & Raw Material with Return & Close actions) */}
              <div className="p-3.5 rounded-xl bg-slate-800/80 border border-slate-700/80">
                <JobKitManager
                  bookingId={selectedBooking.id}
                  professionalId={professional?.id || selectedBooking.professional_id}
                  mode="professional"
                  onUpdated={() => {
                    loadDashboardData();
                  }}
                />
              </div>

              {/* Job Execution Actions based on status */}
              <div className="pt-2 border-t border-slate-800 space-y-3">
                {/* 1. Status: 'assigned' -> Accept / Reject */}
                {selectedBooking.status === 'assigned' && (
                  <div className="space-y-2">
                    <p className="text-xs text-blue-300 bg-blue-950/40 p-2.5 rounded-xl border border-blue-900/50">
                      You have been assigned this service job. Accept to confirm to customer and manager.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAcceptOrReject('accepted')}
                        disabled={actionLoading}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs h-10 rounded-xl"
                      >
                        {actionLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={15} className="mr-1.5" />}
                        <span>Accept Job</span>
                      </Button>
                      <Button
                        onClick={() => handleAcceptOrReject('rejected')}
                        disabled={actionLoading}
                        variant="outline"
                        className="flex-1 border-rose-700/60 text-rose-400 hover:bg-rose-950/40 font-semibold text-xs h-10 rounded-xl"
                      >
                        <XCircle size={15} className="mr-1.5" />
                        <span>Reject Job</span>
                      </Button>
                    </div>
                  </div>
                )}

                {/* 2. Status: 'accepted' -> Start Service (Prompt OTP) */}
                {selectedBooking.status === 'accepted' && (
                  <div className="space-y-2">
                    <p className="text-xs text-purple-300 bg-purple-950/40 p-2.5 rounded-xl border border-purple-900/50">
                      When you arrive at the customer location, ask the customer for their <strong>4-digit Start OTP</strong> to start the service.
                    </p>
                    <Button
                      onClick={() => setShowStartOtpModal(true)}
                      className="w-full bg-primary hover:bg-primary/90 text-white font-semibold text-xs h-11 rounded-xl shadow-md flex items-center justify-center gap-2"
                    >
                      <Play size={16} />
                      <span>Start Service (Enter OTP)</span>
                    </Button>
                  </div>
                )}

                {/* 3. Status: 'in_progress' -> Complete Service */}
                {selectedBooking.status === 'in_progress' && (
                  <div className="space-y-2">
                    <p className="text-xs text-amber-300 bg-amber-950/40 p-2.5 rounded-xl border border-amber-900/50">
                      Service is currently in progress. When done, ask the customer for the <strong>Completion OTP</strong>.
                    </p>
                    <Button
                      onClick={() => {
                        setFinalPriceInput(String(selectedBooking.quoted_price));
                        setShowCompleteModal(true);
                      }}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs h-11 rounded-xl shadow-md flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 size={16} />
                      <span>Complete Service (Enter OTP)</span>
                    </Button>
                  </div>
                )}

                {/* Services Smart Billing: Materials, Unit Tracking, GST Invoice */}
                {(['in_progress', 'completed'].includes(selectedBooking.status) || Boolean(selectedBooking.invoice_id)) && (
                  <div className="pt-2 border-t border-slate-800">
                    <ServiceBillingPanel
                      bookingId={selectedBooking.id}
                      bookingStatus={selectedBooking.status}
                      invoiceId={selectedBooking.invoice_id}
                      role="professional"
                      onChanged={loadDashboardData}
                    />
                  </div>
                )}

                {/* 4. Payment Collection Section: Allowed before payment_status === 'paid' */}
                {selectedBooking.payment_status !== 'paid' && (['in_progress', 'completed'].includes(selectedBooking.status)) && (
                  <div className="p-3.5 rounded-xl bg-slate-800 border border-slate-700 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
                        <IndianRupee size={14} />
                        <span>Collect Payment (₹{selectedBooking.final_price || selectedBooking.quoted_price})</span>
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => {
                          setCashCollectAmount(String(selectedBooking.final_price || selectedBooking.quoted_price));
                          setShowCashConfirmModal(true);
                        }}
                        disabled={actionLoading || !selectedBooking.invoice_id}
                        className={`text-white text-xs font-semibold h-10 rounded-xl ${
                          !selectedBooking.invoice_id
                            ? 'bg-slate-700 opacity-50 cursor-not-allowed text-slate-400'
                            : 'bg-emerald-600 hover:bg-emerald-700'
                        }`}
                      >
                        <IndianRupee size={14} className="mr-1" />
                        <span>Collect Cash</span>
                      </Button>

                      <Button
                        onClick={handleRequestOnlinePayment}
                        disabled={generatingPaymentLink || !selectedBooking.invoice_id}
                        variant="outline"
                        className={`text-xs font-semibold h-10 rounded-xl ${
                          !selectedBooking.invoice_id
                            ? 'border-slate-700 text-slate-500 opacity-50 cursor-not-allowed'
                            : 'border-primary/50 text-primary hover:bg-primary/10'
                        }`}
                      >
                        {generatingPaymentLink ? (
                          <Loader2 size={14} className="animate-spin mr-1" />
                        ) : (
                          <QrCode size={14} className="mr-1" />
                        )}
                        <span>Request Online</span>
                      </Button>
                    </div>

                    {!selectedBooking.invoice_id && (
                      <p className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 p-2.5 rounded-xl flex items-center gap-1.5">
                        <AlertCircle size={14} className="shrink-0 text-amber-400" />
                        <span>Pehle invoice generate karein, phir payment lein.</span>
                      </p>
                    )}

                    {/* Online QR Display if generated */}
                    {paymentLinkData && (
                      <div className="p-4 bg-slate-900 border border-slate-700 rounded-xl text-center space-y-3 mt-3 animate-in fade-in duration-200">
                        <p className="text-xs font-bold text-white">
                          Scan & Pay ₹{paymentLinkData.amount}
                        </p>
                        <div className="p-3 bg-white rounded-xl inline-block shadow-md">
                          <QRCodeSVG
                            value={paymentLinkData.short_url}
                            size={160}
                            level="M"
                            includeMargin={false}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <a
                            href={paymentLinkData.short_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-primary hover:underline break-all"
                          >
                            {paymentLinkData.short_url}
                          </a>
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(paymentLinkData.short_url);
                                toast.success('Payment link copied!');
                              }}
                              className="text-[11px] h-7 px-2.5 border-slate-700"
                            >
                              <Copy size={11} className="mr-1" />
                              <span>Copy Link</span>
                            </Button>
                            <a
                              href={`https://wa.me/?text=${encodeURIComponent(
                                `Hello ${selectedBooking.customer_name}, please pay ₹${paymentLinkData.amount} for your service booking ${selectedBooking.booking_number} using this secure link: ${paymentLinkData.short_url}`
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] h-7 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-medium"
                            >
                              <Share2 size={11} />
                              <span>Share via WhatsApp</span>
                            </a>
                          </div>
                        </div>
                        <p className="text-[10px] text-slate-400">
                          Payment will automatically update once customer pays. Pull-to-refresh to check.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Final state timestamps & customer rating link */}
                {['completed', 'cancelled', 'rejected', 'no_show'].includes(selectedBooking.status) && (
                  <div className="p-3.5 rounded-xl bg-slate-800/50 border border-slate-700/60 text-slate-300 text-xs space-y-2">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                      Job History Timestamps
                    </span>
                    <div className="space-y-1 text-[11px] text-slate-400">
                      {selectedBooking.started_at && (
                        <p>Started: {new Date(selectedBooking.started_at).toLocaleString()}</p>
                      )}
                      {selectedBooking.completed_at && (
                        <p className="text-emerald-400">Completed: {new Date(selectedBooking.completed_at).toLocaleString()}</p>
                      )}
                      {selectedBooking.cancelled_at && (
                        <p className="text-rose-400">Cancelled: {new Date(selectedBooking.cancelled_at).toLocaleString()}</p>
                      )}
                      {selectedBooking.paid_at && (
                        <p className="text-emerald-400">Paid: {new Date(selectedBooking.paid_at).toLocaleString()} via {selectedBooking.payment_mode}</p>
                      )}
                    </div>

                    {selectedBooking.status === 'completed' && (
                      <div className="pt-3 border-t border-slate-700/60 space-y-3">
                        {/* Auto-generated Bill & PDF Share (PATCH #2ad Task D) */}
                        <div className="p-3 bg-slate-800/80 border border-slate-700 rounded-xl space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-white flex items-center gap-1.5">
                              <Receipt size={14} className="text-primary" />
                              <span>Bill & Invoice</span>
                            </span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-semibold border border-emerald-500/30">
                              Auto-generated
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-400">
                            View printed bill, scan QR code, download PDF, or share with the customer.
                          </p>
                          <Button
                            size="sm"
                            onClick={() => {
                              setBillPrintBookingId(selectedBooking.id);
                              setShowBillPrintModal(true);
                            }}
                            className="w-full bg-primary hover:bg-primary/90 text-white font-medium text-xs h-8 rounded-lg shadow-sm flex items-center justify-center gap-2"
                          >
                            <Receipt size={13} />
                            <span>View Bill, QR & Share PDF</span>
                          </Button>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
                            <Star size={14} className="fill-amber-400 text-amber-400" />
                            <span>Customer Rating & Feedback</span>
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400">
                          Ask customer to rate your service or scan this QR code on the spot:
                        </p>
                        <div className="p-3 bg-slate-900 border border-slate-700/80 rounded-xl text-center space-y-2.5">
                          <div className="p-2.5 bg-white rounded-xl inline-block shadow-md">
                            <QRCodeSVG
                              value={`${window.location.origin}/rate/${selectedBooking.id}`}
                              size={130}
                              level="M"
                              includeMargin={false}
                            />
                          </div>
                          <div className="flex items-center justify-center gap-2 pt-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const rateUrl = `${window.location.origin}/rate/${selectedBooking.id}`;
                                navigator.clipboard.writeText(rateUrl);
                                toast.success('Customer rating link copied!');
                              }}
                              className="text-[11px] h-7 px-2.5 border-slate-700 text-slate-200"
                            >
                              <Copy size={11} className="mr-1" />
                              <span>Copy Link</span>
                            </Button>
                            <a
                              href={`https://wa.me/?text=${encodeURIComponent(
                                `Hi ${selectedBooking.customer_name}, thank you for choosing our service! Please take a moment to rate your experience: ${window.location.origin}/rate/${selectedBooking.id}`
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] h-7 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-medium"
                            >
                              <Share2 size={11} />
                              <span>Share WhatsApp</span>
                            </a>
                            <a
                              href={`/rate/${selectedBooking.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] h-7 px-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-md font-medium"
                            >
                              <ExternalLink size={11} />
                              <span>Open</span>
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* START SERVICE OTP MODAL                                  */}
      {/* ======================================================== */}
      {showStartOtpModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 w-full max-w-sm rounded-2xl shadow-2xl p-5 animate-in zoom-in-95 duration-150 text-white">
            <h3 className="text-base font-bold mb-1">Enter Start Service OTP</h3>
            <p className="text-xs text-slate-400 mb-4">
              Ask the customer to read out the 4-digit code shown on their booking confirmation.
            </p>

            <form onSubmit={handleStartServiceSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Start OTP (4 digits)
                </label>
                <Input
                  type="text"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  maxLength={6}
                  value={startOtpInput}
                  onChange={e => setStartOtpInput(e.target.value)}
                  placeholder="e.g. 4821"
                  required
                  autoFocus
                  className="h-11 text-center font-mono text-xl tracking-widest bg-slate-800 border-slate-700 text-white rounded-xl"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowStartOtpModal(false)}
                  disabled={actionLoading}
                  className="flex-1 border-slate-700 text-slate-300 text-xs h-10 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white text-xs font-semibold h-10 rounded-xl"
                >
                  {actionLoading ? <Loader2 size={14} className="animate-spin" /> : 'Confirm & Start'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* COMPLETE SERVICE OTP MODAL                               */}
      {/* ======================================================== */}
      {showCompleteModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 w-full max-w-sm rounded-2xl shadow-2xl p-5 animate-in zoom-in-95 duration-150 text-white">
            <h3 className="text-base font-bold mb-1">Complete Service Verification</h3>
            <p className="text-xs text-slate-400 mb-4">
              Enter the Completion OTP provided by the customer to finish this job.
            </p>

            <form onSubmit={handleCompleteServiceSubmit} className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Completion OTP <span className="text-rose-400">*</span>
                </label>
                <Input
                  type="text"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  maxLength={6}
                  value={completeOtpInput}
                  onChange={e => setCompleteOtpInput(e.target.value)}
                  placeholder="e.g. 7395"
                  required
                  autoFocus
                  className="h-11 text-center font-mono text-xl tracking-widest bg-slate-800 border-slate-700 text-white rounded-xl"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Final Price (₹) <span className="text-slate-400 font-normal">(optional if differs)</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={finalPriceInput}
                  onChange={e => setFinalPriceInput(e.target.value)}
                  placeholder="Quoted price"
                  className="h-10 bg-slate-800 border-slate-700 text-white rounded-xl text-xs"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCompleteModal(false)}
                  disabled={actionLoading}
                  className="flex-1 border-slate-700 text-slate-300 text-xs h-10 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold h-10 rounded-xl"
                >
                  {actionLoading ? <Loader2 size={14} className="animate-spin" /> : 'Confirm & Complete'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* CASH PAYMENT CONFIRM MODAL                               */}
      {/* ======================================================== */}
      {showCashConfirmModal && selectedBooking && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 w-full max-w-sm rounded-2xl shadow-2xl p-5 animate-in zoom-in-95 duration-150 text-white">
            <h3 className="text-base font-bold mb-2">Confirm Cash Collection</h3>
            <p className="text-xs text-slate-300 mb-3 leading-relaxed">
              Have you physically collected cash from <strong>{selectedBooking.customer_name}</strong>?
            </p>

            <div className="space-y-1 mb-3.5">
              <label className="block text-[11px] font-semibold text-slate-300">
                Amount Collected (₹) {selectedBooking.invoice_id && <span className="text-emerald-400 font-normal">(Locked to GST Invoice)</span>}
              </label>
              <Input
                type="number"
                step="0.01"
                disabled={Boolean(selectedBooking.invoice_id)}
                value={selectedBooking.invoice_id ? (selectedBooking.final_price || selectedBooking.quoted_price) : cashCollectAmount}
                onChange={e => setCashCollectAmount(e.target.value)}
                className={`h-10 bg-slate-800 border-slate-700 text-white rounded-xl text-sm font-bold ${
                  selectedBooking.invoice_id ? 'opacity-80 cursor-not-allowed bg-slate-850' : ''
                }`}
              />
              {selectedBooking.invoice_id ? (
                <p className="text-[10px] text-slate-400">
                  This booking has an issued invoice. Cash amount is locked to the invoice total.
                </p>
              ) : (
                <p className="text-[10px] text-slate-400">
                  Verify or update the cash amount physically collected.
                </p>
              )}
            </div>

            {professional?.commission_percent === 0 ? (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-[11px] text-emerald-300 mb-4">
                Recording this cash payment will mark the job as <strong>Paid</strong>. All collections belong directly to your business (0% commission deduction).
              </div>
            ) : (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-[11px] text-amber-300 mb-4">
                Recording this cash payment will mark the job as <strong>Paid</strong> and deduct the {professional?.commission_percent}% business commission from your staff wallet.
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCashConfirmModal(false)}
                disabled={actionLoading}
                className="flex-1 border-slate-700 text-slate-300 text-xs h-10 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCollectCash}
                disabled={actionLoading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold h-10 rounded-xl"
              >
                {actionLoading ? <Loader2 size={14} className="animate-spin" /> : 'Yes, Collected Cash'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Service Bill Print & Share Modal (PATCH #2ad Task D) */}
      {billPrintBookingId && (
        <ServiceBillPrintModal
          bookingId={billPrintBookingId}
          isOpen={showBillPrintModal}
          onClose={() => {
            setShowBillPrintModal(false);
            setBillPrintBookingId(null);
          }}
        />
      )}
    </div>
  );
}
